import useSWR from 'swr';
import { TrustpilotBusinessSummary, TrustpilotReview } from 'lib/trustpilot/types';

export type TrustpilotReviewsApiResponse = {
  reviews: TrustpilotReview[];
  total?: number;
  summary?: TrustpilotBusinessSummary;
};

export type UseTrustpilotReviewsParams = {
  businessUnitId: string;
  maxReviews: number;
  minStars?: number;
  serviceTag: string;
  language: string;
  includeSummary: boolean;
};

const CLIENT_CACHE_DEDUPING_MS = 60 * 60 * 1000;

const trustpilotReviewsFetcher = async (url: string): Promise<TrustpilotReviewsApiResponse> => {
  const res = await fetch(url, { cache: 'default' });
  if (!res.ok) {
    throw new Error('Trustpilot fetch failed');
  }
  return res.json();
};

export const buildTrustpilotReviewsUrl = ({
  businessUnitId,
  maxReviews,
  minStars,
  serviceTag,
  language,
  includeSummary,
}: UseTrustpilotReviewsParams): string => {
  const params = new URLSearchParams({
    businessUnitId,
    maxReviews: String(maxReviews),
    serviceTag,
    language,
  });

  if (minStars != null) {
    params.set('minStars', String(minStars));
  }

  if (includeSummary) {
    params.append('includeSummary', 'true');
  }

  return `/api/trustpilot/reviews?${params}`;
};

const useTrustpilotReviews = (params: UseTrustpilotReviewsParams) => {
  const swrKey = params.serviceTag ? buildTrustpilotReviewsUrl(params) : null;

  const { data, error, isLoading, isValidating } = useSWR<TrustpilotReviewsApiResponse>(
    swrKey,
    trustpilotReviewsFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: CLIENT_CACHE_DEDUPING_MS,
      keepPreviousData: true,
    }
  );

  // SWR can report isLoading=false before the first response; treat that as pending.
  const isPending = Boolean(swrKey) && data === undefined && !error;

  return {
    data,
    error,
    isLoading: isLoading || isValidating || isPending,
  };
};

export default useTrustpilotReviews;
